/**
 * Entry point: picks a mode from the URL.
 *   ?mode=creator  character creator (face, body, hair, clothes) → Life Card → Start life
 *   (default)      the district, living the life you started
 * Without a started life there's nothing to live yet, so a first visit goes to the creator.
 * Dev views (?view=, ?cam=free, ?avatar=, ?mode=street) still open the street on its own.
 * ?slot=<name> plays a separate save in this tab (see slot.ts); ?slot=main goes back to the normal one.
 */
import { slotKey } from './slot';
import { installRotate } from './mobile';
import { syncOnStart } from './net/account';
import { initTelemetry, track } from './net/telemetry';
import { shareAudio } from './life/audiofocus';
import { connectionScreens, watchConnection } from './net/health';

installRotate();
shareAudio(); // (the game's sound plays over the player's own music, never stops it: life/audiofocus)
initTelemetry(); // (live build only, and only once its keys are set)
watchConnection(); connectionScreens(); // (no connection: a screen that says so, and goes by itself when it's back: net/health)

const params = new URLSearchParams(location.search);
const mode = params.get('mode');
// (a test identity for multiplayer, ?pid=: kept for the tab, through the creator → street hop: net/presence playerId)
try { if (params.get('pid')) sessionStorage.setItem('unfold.pid', params.get('pid')!); } catch { /* storage blocked */ }
// (signed in: the account's life, if another device played on since; quick, and offline it's this device's)
const boot = (window as unknown as { __boot?: { stage(l: string, s: string, p: number): void; done(): void; fail?(): void } }).__boot;
/** a part of the game that didn't download: "No internet connection" (index.html checks first), never a blank page */
const failed = () => (boot?.fail ? boot.fail() : boot?.done());
const openCreator = () => { track('creator_opened'); boot?.stage('Setting up the studio', 'The character creator', 40); void import('./creator/creator').then(() => boot?.done(), failed); };
boot?.stage('Opening your life', 'Checking your save', 8);
await syncOnStart();
let started = false;
try { started = !!localStorage.getItem(slotKey('lifesim.life.v1')); } catch { /* storage blocked */ }
const dev = params.has('view') || params.get('cam') === 'free' || params.has('avatar') || mode === 'street';
// (dev: ?mode=roof, the folding roof on its own: src/dev/roof.ts)
if (import.meta.env.DEV && mode === 'roof') void import('./dev/roof');
// (dev: ?mode=shooter, the VR headset's game on its own: src/vr/shooter.ts)
else if (import.meta.env.DEV && mode === 'shooter') void import('./vr/shooter').then((m) => { (window as unknown as { __boot?: { done(): void } }).__boot?.done(); void m.putOnHeadset({ lite: /lite=1/.test(location.search), touch: matchMedia('(pointer: coarse)').matches, onExit: () => location.reload() }); });
// (one life at a time: with a life going, the creator is reached only by ending it, Support → Your life; dev can still overwrite)
else if (mode === 'creator' && started && !import.meta.env.DEV) location.replace(import.meta.env.BASE_URL);
else if (mode === 'creator') openCreator();
// (a first visit: the creator right here, under the same loading screen, not a reload into a second one)
else if (!started && !dev) { params.set('mode', 'creator'); history.replaceState(null, '', `${location.pathname}?${params}`); openCreator(); }
else { boot?.stage('Laying the streets', 'Building the city', 22); void import('./street').catch(failed); }
